import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Globe, Plus, Trash2, Save, Coins, Smartphone, PhoneCall, Syringe, Sparkles, Landmark, ChevronLeft } from "lucide-react";
import { Link } from "react-router-dom";

const BLANK = {
  country: "", city: "",
  currency: { code: "", name: "", symbol: "", tips: "" },
  apps: [],
  emergency_numbers: [],
  vaccinations: [],
  culture_shock: "",
  landmarks: [],
};

export default function DestinationAdmin() {
  const { user } = useAuth();
  const canEdit = ["FOUNDER", "BDOM", "PCWM"].includes(user.real_role);
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/destination-content/all");
      setRows(data || []);
      if (data?.length && !active) {
        setActive(data[0].id);
        loadForm(data[0]);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    }
  };
  useEffect(() => { if (canEdit) load(); }, [canEdit]);

  const loadForm = (d) => setForm({
    country: d.country || "",
    city: d.city || "",
    currency: d.currency || { code: "", name: "", symbol: "", tips: "" },
    apps: d.apps || [],
    emergency_numbers: d.emergency_numbers || [],
    vaccinations: d.vaccinations || [],
    culture_shock: d.culture_shock || "",
    landmarks: d.landmarks || [],
  });

  const pick = (d) => { setActive(d.id); loadForm(d); };
  const startNew = () => { setActive("__new__"); setForm(BLANK); };

  const save = async () => {
    if (!form.country.trim() || !form.city.trim()) {
      toast.error("Country and city are required"); return;
    }
    setBusy(true);
    try {
      await api.post("/destination-content", form);
      toast.success("Saved — visible to participants immediately");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const del = async () => {
    if (!active || active === "__new__") return;
    if (!window.confirm(`Delete destination content for ${form.city}, ${form.country}?`)) return;
    try {
      await api.delete(`/destination-content/${active}`);
      toast.success("Deleted");
      setActive(null);
      setForm(BLANK);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // List editors -----------------------------------------------
  const addApp = () => setForm(f => ({ ...f, apps: [...f.apps, { name: "", category: "", url: "" }] }));
  const rmApp = (i) => setForm(f => ({ ...f, apps: f.apps.filter((_, idx) => idx !== i) }));
  const setApp = (i, k, v) => setForm(f => ({ ...f, apps: f.apps.map((a, idx) => idx === i ? { ...a, [k]: v } : a) }));

  const addEmg = () => setForm(f => ({ ...f, emergency_numbers: [...f.emergency_numbers, { label: "", number: "" }] }));
  const rmEmg = (i) => setForm(f => ({ ...f, emergency_numbers: f.emergency_numbers.filter((_, idx) => idx !== i) }));
  const setEmg = (i, k, v) => setForm(f => ({ ...f, emergency_numbers: f.emergency_numbers.map((a, idx) => idx === i ? { ...a, [k]: v } : a) }));

  const addVac = () => setForm(f => ({ ...f, vaccinations: [...f.vaccinations, { name: "", notes: "" }] }));
  const rmVac = (i) => setForm(f => ({ ...f, vaccinations: f.vaccinations.filter((_, idx) => idx !== i) }));
  const setVac = (i, k, v) => setForm(f => ({ ...f, vaccinations: f.vaccinations.map((a, idx) => idx === i ? { ...a, [k]: v } : a) }));

  const addLm = () => setForm(f => ({ ...f, landmarks: [...f.landmarks, { name: "", description: "" }] }));
  const rmLm = (i) => setForm(f => ({ ...f, landmarks: f.landmarks.filter((_, idx) => idx !== i) }));
  const setLm = (i, k, v) => setForm(f => ({ ...f, landmarks: f.landmarks.map((a, idx) => idx === i ? { ...a, [k]: v } : a) }));

  if (!canEdit) {
    return <div className="p-8 text-center text-[color:var(--aii-muted)]">Founder / BDOM / PCWM only.</div>;
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">
            <Link to="/settings" className="hover:text-brand inline-flex items-center gap-1"><ChevronLeft size={11}/> Settings</Link>
          </div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><Globe className="text-brand"/> Destination Content</h1>
          <p className="text-sm text-[color:var(--aii-text-2)] mt-1">Curate per country + city. Participants see this in their portal &rarr; Destination tab based on their placement preference.</p>
        </div>
        <button onClick={startNew} data-testid="dest-new"
          className="bg-brand text-white font-semibold px-4 py-2 rounded-md hover:bg-brand-dark flex items-center gap-1.5">
          <Plus size={14}/> Add destination
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-4">
        {/* Sidebar: destination list */}
        <div className="aii-card p-3 space-y-1 h-fit" data-testid="dest-sidebar">
          {rows.length === 0 && <div className="text-xs text-[color:var(--aii-muted)] px-2 py-4 text-center">No destinations yet.</div>}
          {rows.map(r => (
            <button key={r.id} onClick={() => pick(r)} data-testid={`dest-item-${r.id}`}
              className={`w-full text-left px-3 py-2 rounded-md text-sm ${active === r.id ? "bg-brand text-white" : "hover:bg-[color:var(--aii-green-tint)]"}`}>
              <div className="font-semibold">{r.city}</div>
              <div className={`text-[11px] ${active === r.id ? "text-white/80" : "text-[color:var(--aii-muted)]"}`}>{r.country}</div>
            </button>
          ))}
        </div>

        {/* Editor */}
        {(active) && (
          <div className="aii-card p-5 space-y-5" data-testid="dest-editor">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block">
                <div className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">Country</div>
                <input value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))} className="input-aii w-full mt-1" placeholder="India" data-testid="dest-country"/>
              </label>
              <label className="block">
                <div className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">City</div>
                <input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} className="input-aii w-full mt-1" placeholder="Jaipur" data-testid="dest-city"/>
              </label>
            </div>

            {/* Currency */}
            <section>
              <h3 className="font-display font-semibold flex items-center gap-2 mb-2"><Coins size={16} className="text-brand"/> Currency</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <input placeholder="Code (INR)" value={form.currency.code} onChange={e => setForm(f => ({ ...f, currency: { ...f.currency, code: e.target.value } }))} className="input-aii" data-testid="dest-cur-code"/>
                <input placeholder="Name (Indian Rupee)" value={form.currency.name} onChange={e => setForm(f => ({ ...f, currency: { ...f.currency, name: e.target.value } }))} className="input-aii" data-testid="dest-cur-name"/>
                <input placeholder="Symbol (₹)" value={form.currency.symbol} onChange={e => setForm(f => ({ ...f, currency: { ...f.currency, symbol: e.target.value } }))} className="input-aii" data-testid="dest-cur-sym"/>
              </div>
              <textarea rows={2} placeholder="Cash / ATM / card tips…" value={form.currency.tips} onChange={e => setForm(f => ({ ...f, currency: { ...f.currency, tips: e.target.value } }))} className="input-aii w-full mt-2" data-testid="dest-cur-tips"/>
            </section>

            {/* Apps */}
            <ListEditor
              title="Handy apps" icon={Smartphone} testid="dest-apps"
              rows={form.apps} onAdd={addApp} onRemove={rmApp}
              columns={[
                { k: "name", ph: "Name (Uber)", cols: "sm:col-span-1" },
                { k: "category", ph: "Category (Transport)", cols: "sm:col-span-1" },
                { k: "url", ph: "URL (https://…)", cols: "sm:col-span-1" },
              ]}
              onChange={setApp}
            />

            {/* Emergency numbers */}
            <ListEditor
              title="Emergency numbers" icon={PhoneCall} tone="red" testid="dest-emg"
              rows={form.emergency_numbers} onAdd={addEmg} onRemove={rmEmg}
              columns={[
                { k: "label", ph: "Label (Police)", cols: "sm:col-span-2" },
                { k: "number", ph: "Number (100)", cols: "sm:col-span-1" },
              ]}
              onChange={setEmg}
            />

            {/* Vaccinations */}
            <ListEditor
              title="Recommended vaccinations" icon={Syringe} testid="dest-vac"
              rows={form.vaccinations} onAdd={addVac} onRemove={rmVac}
              columns={[
                { k: "name", ph: "Name (Hepatitis A)", cols: "sm:col-span-1" },
                { k: "notes", ph: "Notes / advice", cols: "sm:col-span-2" },
              ]}
              onChange={setVac}
            />

            {/* Culture shock */}
            <section>
              <h3 className="font-display font-semibold flex items-center gap-2 mb-2"><Sparkles size={16} className="text-brand"/> Culture shock — heads-up</h3>
              <textarea rows={5} value={form.culture_shock} onChange={e => setForm(f => ({ ...f, culture_shock: e.target.value }))}
                placeholder="Head-wobble means yes. Remove shoes at temples. Bargain in markets…"
                className="input-aii w-full" data-testid="dest-culture"/>
            </section>

            {/* Landmarks */}
            <ListEditor
              title="Must-see landmarks" icon={Landmark} testid="dest-lm"
              rows={form.landmarks} onAdd={addLm} onRemove={rmLm}
              columns={[
                { k: "name", ph: "Name (Amber Fort)", cols: "sm:col-span-1" },
                { k: "description", ph: "Short description", cols: "sm:col-span-2" },
              ]}
              onChange={setLm}
            />

            {/* Actions */}
            <div className="flex justify-between pt-4 border-t border-[color:var(--aii-border)]">
              {active !== "__new__" && user.real_role === "FOUNDER" ? (
                <button onClick={del} className="text-sm px-3 py-2 border border-red-300 text-red-700 rounded hover:bg-red-50 flex items-center gap-1.5" data-testid="dest-del">
                  <Trash2 size={13}/> Delete
                </button>
              ) : <div/>}
              <button onClick={save} disabled={busy} data-testid="dest-save"
                className="bg-brand text-white font-semibold px-4 py-2 rounded-md hover:bg-brand-dark disabled:opacity-50 flex items-center gap-1.5">
                <Save size={14}/> {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        )}
        {!active && (
          <div className="aii-card p-8 text-center text-sm text-[color:var(--aii-muted)]">
            Select a destination to edit, or click <b>Add destination</b>.
          </div>
        )}
      </div>
    </div>
  );
}

function ListEditor({ title, icon: Icon, testid, rows, columns, onAdd, onRemove, onChange, tone }) {
  const toneCls = tone === "red" ? "text-red-600" : "text-brand";
  return (
    <section data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <h3 className={`font-display font-semibold flex items-center gap-2 ${tone === "red" ? "text-red-800" : ""}`}>
          <Icon size={16} className={toneCls}/> {title}
        </h3>
        <button onClick={onAdd} className="text-xs px-2.5 py-1 border border-[color:var(--aii-border)] rounded hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1" data-testid={`${testid}-add`}>
          <Plus size={11}/> Add
        </button>
      </div>
      <div className="space-y-2">
        {rows.length === 0 && <div className="text-xs text-[color:var(--aii-muted)] italic">None yet.</div>}
        {rows.map((r, i) => (
          <div key={`${testid}-${i}`} className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-start" data-testid={`${testid}-row-${i}`}>
            {columns.map((c) => (
              <input key={c.k} className={`input-aii ${c.cols || ""}`} placeholder={c.ph}
                value={r[c.k] || ""} onChange={e => onChange(i, c.k, e.target.value)}
                data-testid={`${testid}-${i}-${c.k}`}/>
            ))}
            <button onClick={() => onRemove(i)} className="text-[color:var(--aii-muted)] hover:text-red-600 self-center sm:justify-self-end" title="Remove" data-testid={`${testid}-rm-${i}`}>
              <Trash2 size={14}/>
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}
